<script>
import axios from "axios";
import { nextTick } from "vue";
import YoutubeVideo from "@/components/VideoPlayer.vue";
import AudioPlayer from "@/components/AudioPlayer.vue";
import HawamPlayer from "@/components/HawamPlayer.vue";
import PdfLearningViewer from "@/components/pdf/PdfLearningViewer.vue";
import youtubeIcon from "@/assets/Icons/youtube.vue";
import pdf from "@/assets/Icons/pdf.vue";
import audio from "@/assets/Icons/audio.vue";
import { useAcademicYears } from "@/composables/useAcademicYears";

export default {
  name: "contents",
  components: { AudioPlayer, YoutubeVideo, HawamPlayer, PdfLearningViewer, youtubeIcon, pdf, audio },
  setup() {
    const { getAcademicYearName, fetchAcademicYears } = useAcademicYears();
    return { getAcademicYearName, fetchAcademicYears };
  },
  data() {
    return {
      education_level: JSON.parse(localStorage.getItem("user")).education_level,
      user: JSON.parse(localStorage.getItem("user") || "{}"),
      branches: [],
      units: [],
      // navigation
      view: "browse",            // 'browse' | 'lesson'
      openedBranchId: null,
      expandedUnitId: null,      // which unit card is expanded in browse view
      activeUnit: null,          // unit that owns the open lesson
      activeLesson: null,        // lesson being viewed in lesson view
      // content player
      content: null,
      selectedContentId: null,
      videoId: "",
      activePlayer: "youtube",
      // loading flags
      loadingBranches: false,
      loadingUnits: false,
      loadingContents: false,
      // Subscription gate (Feature 7): set when the API returns 403
      // subscription_required, so the browse view shows a locked state instead of
      // an empty one. Backend enforcement is authoritative — this is just the UI.
      subscriptionLocked: false,
      subscriptionMessage: "للوصول إلى محتوى الحصص، يجب الاشتراك في هذه السنة الدراسية.",
    };
  },
  computed: {
    academicYearLabel() {
      return this.getAcademicYearName(this.user?.academic_year_id);
    },
    // Existing authenticated-student identifier for the anti-piracy video
    // watermark. Uses the student's own reservation number (what they log in
    // with) or, failing that, their user id — never a newly generated value.
    // Returns null when there is no authenticated student (e.g. logged out),
    // so no fake id is ever shown.
    studentWatermarkId() {
      const u = this.user;
      const id = u && (u.reservation_number ?? u.id);
      return (id === 0 || id) ? id : null;
    },
    // PDF watermark identifier: the student's booking/reservation number
    // ONLY (رقم حجز الطالب) — unlike studentWatermarkId above (used for the
    // video watermark), this deliberately does NOT fall back to the user id,
    // and never phone/parent-phone/WhatsApp. Empty/null when the student has
    // no reservation yet — PdfLearningViewer already renders no watermark at
    // all for an empty watermark-lines value, which is the correct safe
    // behavior here (never fall back to showing the phone number).
    pdfWatermarkId() {
      const u = this.user;
      const id = u && u.reservation_number;
      return (id === 0 || id) ? id : null;
    },
    selectedBranch() {
      return this.branches.find((b) => b.id === this.openedBranchId) || null;
    },
    // "مراجعة" is a title convention (no backend flag) — split it out so it can
    // be rendered as a dedicated review section instead of a normal unit.
    normalUnits() {
      return this.units.filter((u) => !this.isReview(u));
    },
    reviewUnits() {
      return this.units.filter((u) => this.isReview(u));
    },
    lessonList() {
      return (this.activeUnit && this.activeUnit.lessons) || [];
    },
    activeLessonIndex() {
      if (!this.activeLesson) return -1;
      return this.lessonList.findIndex((l) => l.id === this.activeLesson.id);
    },
    prevLesson() {
      const i = this.activeLessonIndex;
      return i > 0 ? this.lessonList[i - 1] : null;
    },
    nextLesson() {
      const i = this.activeLessonIndex;
      return i > -1 && i < this.lessonList.length - 1 ? this.lessonList[i + 1] : null;
    },
    lessonContents() {
      return (this.activeLesson && this.activeLesson.contents) || [];
    },
  },
  methods: {
    isReview(item) {
      return !!item && typeof item.title === "string" && item.title.includes("مراجعة");
    },
    unitLessonCount(unit) {
      if (unit && unit.children_count != null) return unit.children_count;
      if (unit && Array.isArray(unit.lessons)) return unit.lessons.length;
      return null;
    },

    /* ── Data fetching (endpoints/params unchanged — same as before) ── */
    // True when the API refused a request because the student has no active
    // subscription for this academic year (SubscriptionGate 403).
    isSubscriptionError(e) {
      const r = e && e.response;
      return !!r && r.status === 403 && r.data && r.data.code === "subscription_required";
    },
    handleSubscriptionError(e) {
      if (this.isSubscriptionError(e)) {
        this.subscriptionLocked = true;
        if (e.response.data.message) this.subscriptionMessage = e.response.data.message;
        return true;
      }
      return false;
    },

    fetchBranch() {
      this.loadingBranches = true;
      const params = { type: 1, education_level: this.education_level };
      axios
        .get("/web/lessons", { params })
        .then((response) => {
          this.subscriptionLocked = false;
          this.branches = response.data.data;
          // Auto-select the first branch so the app opens straight into units,
          // like a native course screen. User can still switch branches.
          if (this.branches.length && this.openedBranchId == null) {
            this.selectBranch(this.branches[0]);
          }
        })
        .catch((e) => { this.handleSubscriptionError(e); this.branches = []; })
        .finally(() => { this.loadingBranches = false; });
    },

    fetchUnits(branchId) {
      this.loadingUnits = true;
      axios
        .get(`/web/lessons?type=2&education_level=${this.education_level}&parent_id=${branchId}`)
        .then((response) => {
          this.subscriptionLocked = false;
          this.units = response.data.data.map((u) => ({ ...u, lessons: [], _lessonsLoaded: false }));
        })
        .catch((e) => { this.handleSubscriptionError(e); this.units = []; })
        .finally(() => { this.loadingUnits = false; });
    },

    async fetchLessons(unit) {
      try {
        const response = await axios.get(
          `/web/lessons?type=3&education_level=${this.education_level}&parent_id=${unit.id}`
        );
        unit.lessons = response.data.data.map((l) => ({ ...l, contents: [], _contentsLoaded: false }));
        unit._lessonsLoaded = true;
        await nextTick();
      } catch (e) {
        this.handleSubscriptionError(e);
        unit.lessons = [];
        unit._lessonsLoaded = true;
      }
    },

    async fetchContents(lesson) {
      this.loadingContents = true;
      try {
        const params = { education_level: this.education_level, lesson_id: lesson.id };
        const response = await axios.get("/web/contents", { params });
        lesson.contents = response.data.data;
        lesson._contentsLoaded = true;
        await nextTick();
      } catch (e) {
        this.handleSubscriptionError(e);
        lesson.contents = [];
        lesson._contentsLoaded = true;
      } finally {
        this.loadingContents = false;
      }
    },

    /* ── Browse-view navigation ── */
    selectBranch(branch) {
      if (this.openedBranchId === branch.id) return;
      this.openedBranchId = branch.id;
      this.expandedUnitId = null;
      this.units = [];
      this.fetchUnits(branch.id);
    },

    toggleUnit(unit) {
      if (this.expandedUnitId === unit.id) {
        this.expandedUnitId = null;
        return;
      }
      this.expandedUnitId = unit.id;
      if (!unit._lessonsLoaded) this.fetchLessons(unit);
    },

    /* ── Lesson view ── */
    async openLesson(unit, lesson) {
      this.activeUnit = unit;
      this.activeLesson = lesson;
      this.view = "lesson";
      this.content = null;
      this.selectedContentId = null;
      this.videoId = "";
      if (!lesson._contentsLoaded) await this.fetchContents(lesson);
      this.autoSelectContent(lesson);
      this.scrollTop();
    },

    autoSelectContent(lesson) {
      const list = lesson.contents || [];
      const first = list.find((c) => c.type === "video") || list[0];
      if (first) this.showContent(first);
    },

    goToLesson(lesson) {
      if (lesson) this.openLesson(this.activeUnit, lesson);
    },

    backToBrowse() {
      this.view = "browse";
      if (this.activeUnit) this.expandedUnitId = this.activeUnit.id;
      this.scrollTop();
    },

    showContent(content) {
      this.selectedContentId = content.id;
      this.content = null;
      // brief null flip forces player components to remount cleanly
      setTimeout(() => {
        this.content = content;
        if (content.type === "video" && content.link) {
          const match = content.link.match(/(?:v=|\/embed\/|\.be\/|watch\?v=)([A-Za-z0-9_-]{11})/);
          this.videoId = match && match[1] ? match[1] : "";
        } else {
          this.videoId = "";
        }
        this.activePlayer = "youtube";
        nextTick(() => {
          if (this.$refs.stage && this.view === "lesson") {
            this.$refs.stage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      }, 10);
    },

    scrollTop() {
      nextTick(() => {
        try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) { window.scrollTo(0, 0); }
      });
    },

    contentTypeLabel(type) {
      if (type === "video") return "فيديو";
      if (type === "audio") return "صوت";
      if (type === "pdf") return "ملف PDF";
      return "ملف";
    },
  },
  mounted() {
    this.fetchAcademicYears();
    this.fetchBranch();
  },
};
</script>

<template>
  <div class="cx-page">

    <!-- ══════════════════ BROWSE VIEW ══════════════════ -->
    <template v-if="view === 'browse'">

      <!-- Course header -->
      <header class="cx-hero">
        <div class="cx-hero-badge">📚 محتوى الكورس</div>
        <h1 class="cx-hero-title">{{ selectedBranch ? selectedBranch.title : academicYearLabel }}</h1>
        <p class="cx-hero-sub" v-if="selectedBranch && selectedBranch.description">
          {{ selectedBranch.description }}
        </p>
        <div class="cx-hero-meta" v-if="selectedBranch">
          <span class="cx-hero-chip" v-if="academicYearLabel">🎓 {{ academicYearLabel }}</span>
          <span class="cx-hero-chip" v-if="unitLessonCount(selectedBranch) != null">
            📦 {{ unitLessonCount(selectedBranch) }} وحدة
          </span>
        </div>
      </header>

      <div class="cx-body">

        <!-- Subscription locked (Feature 7): shown when the API returns 403
             subscription_required. Backend enforcement is authoritative. -->
        <div v-if="subscriptionLocked" class="cx-locked">
          <div class="cx-locked-icon">🔒</div>
          <h2 class="cx-locked-title">المحتوى مقفل</h2>
          <p class="cx-locked-msg">{{ subscriptionMessage }}</p>
          <router-link to="/subscribe" class="cx-locked-btn">الاشتراك الآن</router-link>
        </div>

        <template v-else>
        <!-- Branch selector (only when more than one branch/term exists) -->
        <div class="cx-branch-strip" v-if="branches.length > 1">
          <button
            v-for="branch in branches"
            :key="branch.id"
            class="cx-branch-chip"
            :class="{ active: openedBranchId === branch.id }"
            @click="selectBranch(branch)"
          >{{ branch.title }}</button>
        </div>

        <!-- Loading units -->
        <div v-if="loadingUnits" class="cx-loading">
          <span class="cx-spinner"></span>
          <span>جاري تحميل الوحدات...</span>
        </div>

        <template v-else>
          <!-- Review section (visually separated) -->
          <section class="cx-review" v-if="reviewUnits.length">
            <div class="cx-section-label"><span class="cx-rot">🔄</span> مراجعة</div>
            <div
              v-for="unit in reviewUnits"
              :key="'rev-' + unit.id"
              class="cx-unit cx-unit--review"
              :class="{ expanded: expandedUnitId === unit.id }"
            >
              <button class="cx-unit-head" @click="toggleUnit(unit)">
                <span class="cx-unit-icon">🔄</span>
                <span class="cx-unit-titles">
                  <span class="cx-unit-title">{{ unit.title }}</span>
                  <span class="cx-unit-sub" v-if="unitLessonCount(unit) != null">
                    {{ unitLessonCount(unit) }} دروس
                  </span>
                </span>
                <span class="cx-chevron">˅</span>
              </button>
              <div class="cx-lessons" v-if="expandedUnitId === unit.id">
                <button
                  v-for="lesson in unit.lessons"
                  :key="lesson.id"
                  class="cx-lesson-row"
                  @click="openLesson(unit, lesson)"
                >
                  <span class="cx-play">▶</span>
                  <span class="cx-lesson-info">
                    <span class="cx-lesson-title">{{ lesson.title }}</span>
                    <span class="cx-lesson-desc" v-if="lesson.description">{{ lesson.description }}</span>
                  </span>
                  <span class="cx-go">‹</span>
                </button>
                <div v-if="unit._lessonsLoaded && !unit.lessons.length" class="cx-empty-inline">
                  لا توجد دروس بعد
                </div>
                <div v-else-if="!unit._lessonsLoaded" class="cx-empty-inline">جاري التحميل...</div>
              </div>
            </div>
          </section>

          <!-- Units -->
          <section class="cx-units" v-if="normalUnits.length">
            <div class="cx-section-label" v-if="reviewUnits.length">الوحدات</div>
            <div
              v-for="unit in normalUnits"
              :key="unit.id"
              class="cx-unit"
              :class="{ expanded: expandedUnitId === unit.id }"
            >
              <button class="cx-unit-head" @click="toggleUnit(unit)">
                <span class="cx-unit-icon">📘</span>
                <span class="cx-unit-titles">
                  <span class="cx-unit-title">{{ unit.title }}</span>
                  <span class="cx-unit-sub">
                    <template v-if="unitLessonCount(unit) != null">{{ unitLessonCount(unit) }} دروس</template>
                    <template v-else-if="unit.description">{{ unit.description }}</template>
                  </span>
                </span>
                <span class="cx-chevron">˅</span>
              </button>

              <transition name="cx-expand">
                <div class="cx-lessons" v-if="expandedUnitId === unit.id">
                  <button
                    v-for="lesson in unit.lessons"
                    :key="lesson.id"
                    class="cx-lesson-row"
                    @click="openLesson(unit, lesson)"
                  >
                    <span class="cx-play">▶</span>
                    <span class="cx-lesson-info">
                      <span class="cx-lesson-title">{{ lesson.title }}</span>
                      <span class="cx-lesson-desc" v-if="lesson.description">{{ lesson.description }}</span>
                    </span>
                    <span class="cx-go">‹</span>
                  </button>
                  <div v-if="unit._lessonsLoaded && !unit.lessons.length" class="cx-empty-inline">
                    لا توجد دروس بعد
                  </div>
                  <div v-else-if="!unit._lessonsLoaded" class="cx-empty-inline">
                    <span class="cx-spinner cx-spinner--sm"></span> جاري تحميل الدروس...
                  </div>
                </div>
              </transition>
            </div>
          </section>

          <!-- Empty state -->
          <div v-if="!units.length && openedBranchId" class="cx-empty">
            <div class="cx-empty-icon">🗂️</div>
            <p>لا توجد وحدات متاحة في هذا الكورس بعد</p>
          </div>
          <div v-if="!openedBranchId && !loadingBranches && !branches.length" class="cx-empty">
            <div class="cx-empty-icon">🗂️</div>
            <p>لا يوجد محتوى متاح حالياً</p>
          </div>
        </template>
        </template>
      </div>
    </template>

    <!-- ══════════════════ LESSON VIEW ══════════════════ -->
    <template v-else>
      <div class="cx-lesson-view">

        <!-- Sticky back bar -->
        <div class="cx-topbar">
          <button class="cx-back" @click="backToBrowse">‹</button>
          <div class="cx-topbar-title">{{ activeLesson && activeLesson.title }}</div>
        </div>

        <!-- Main stage: full-width player -->
        <div class="cx-stage" ref="stage">
          <div v-if="content && content.type === 'video'" class="cx-player">
            <YoutubeVideo
              v-show="activePlayer === 'youtube'"
              :videoId="videoId"
              :type="content.type"
              :student-id="studentWatermarkId"
              :poster="content.poster"
              :key="'yt-' + videoId"
              ref="youtube"
            />
            <HawamPlayer
              v-show="activePlayer === 'hawam'"
              :videoId="videoId"
              :type="content.type"
              :student-id="studentWatermarkId"
              :poster="content.poster"
              :key="'hw-' + videoId"
              ref="hawam"
            />
          </div>

          <AudioPlayer
            v-else-if="content && content.type === 'audio'"
            :link="content.file || ''"
            :key="content.file"
          />

          <div v-else-if="content && content.type === 'pdf'" class="cx-pdf">
            <PdfLearningViewer
              :pdf-url="content.file || ''"
              :content-id="content.id"
              :read-only="false"
              :watermark-enabled="true"
              :watermark-lines="[pdfWatermarkId]"
            />
          </div>

          <div v-else class="cx-stage-empty">
            <div class="cx-empty-icon">🎬</div>
            <p>اختر محتوى من القائمة أدناه</p>
          </div>
        </div>

        <!-- Player switch for video -->
        <div class="cx-switch" v-if="content && content.type === 'video'">
          <button :class="{ active: activePlayer === 'youtube' }" @click="activePlayer = 'youtube'">▶ المشغّل الأول</button>
          <button :class="{ active: activePlayer === 'hawam' }" @click="activePlayer = 'hawam'">▶ المشغّل الثاني</button>
        </div>

        <!-- Video + PDF split-screen: the attached PDF renders BELOW the video,
             reusing the exact same PdfLearningViewer as standalone PDF content.
             Rendered ONLY when a PDF is attached (no empty section otherwise), and
             OUTSIDE the video player, so it never enters the video's fullscreen. -->
        <div
          v-if="content && content.type === 'video' && content.pdf_file"
          class="cx-video-pdf cx-pdf"
        >
          <PdfLearningViewer
            :pdf-url="content.pdf_file"
            :content-id="content.id"
            :read-only="false"
            :watermark-enabled="true"
            :watermark-lines="[pdfWatermarkId]"
            :key="'vpdf-' + content.id"
          />
        </div>

        <!-- Lesson meta -->
        <div class="cx-lesson-meta">
          <h2 class="cx-lesson-h">{{ activeLesson && activeLesson.title }}</h2>
          <p class="cx-lesson-p" v-if="activeLesson && activeLesson.description">
            {{ activeLesson.description }}
          </p>
        </div>

        <!-- Lesson content list -->
        <div class="cx-content-list">
          <div class="cx-section-label">محتوى الدرس</div>

          <div v-if="loadingContents" class="cx-loading">
            <span class="cx-spinner"></span><span>جاري تحميل المحتوى...</span>
          </div>

          <template v-else>
            <button
              v-for="c in lessonContents"
              :key="c.id"
              class="cx-content-row"
              :class="{ active: selectedContentId === c.id }"
              @click="showContent(c)"
            >
              <span class="cx-content-ic">
                <template v-if="c.type === 'video'"><youtubeIcon /></template>
                <template v-else-if="c.type === 'audio'"><audio /></template>
                <template v-else-if="c.type === 'pdf'"><pdf /></template>
                <template v-else>📁</template>
              </span>
              <span class="cx-content-info">
                <span class="cx-content-title">{{ c.title }}</span>
                <span class="cx-content-type">{{ contentTypeLabel(c.type) }}</span>
              </span>
              <span class="cx-content-state" v-if="selectedContentId === c.id">يُعرض الآن</span>
            </button>

            <div v-if="!lessonContents.length" class="cx-empty-inline">
              لا يوجد محتوى في هذا الدرس بعد
            </div>
          </template>
        </div>

        <!-- Prev / next lesson -->
        <div class="cx-nav">
          <button class="cx-nav-btn" :disabled="!prevLesson" @click="goToLesson(prevLesson)">
            <span class="cx-nav-dir">الدرس السابق</span>
            <span class="cx-nav-name" v-if="prevLesson">{{ prevLesson.title }}</span>
          </button>
          <button class="cx-nav-btn cx-nav-btn--next" :disabled="!nextLesson" @click="goToLesson(nextLesson)">
            <span class="cx-nav-dir">الدرس التالي</span>
            <span class="cx-nav-name" v-if="nextLesson">{{ nextLesson.title }}</span>
          </button>
        </div>

      </div>
    </template>

  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap');

*, *::before, *::after { box-sizing: border-box; }

.cx-page {
  direction: rtl;
  font-family: 'Cairo', sans-serif;
  background: #F5F7FB;
  min-height: calc(100vh - 70px);
  overflow-x: hidden;
  padding-bottom: 88px;             /* clear the fixed bottom nav */
  color: #1A1A1A;
}

/* ─────────────── Course hero ─────────────── */
.cx-hero {
  background: linear-gradient(160deg, #5B5FEF 0%, #6C55F5 55%, #7C4DFF 100%);
  border-radius: 0 0 26px 26px;
  box-shadow: 0 10px 30px rgba(91,95,239,.22);
  padding: 1.4rem 1.15rem 1.6rem;
  color: #fff;
}
.cx-hero-badge {
  display: inline-block;
  background: rgba(255,255,255,.18);
  border: 1px solid rgba(255,255,255,.28);
  border-radius: 99px;
  padding: .22rem .8rem;
  font-size: .74rem;
  font-weight: 700;
  margin-bottom: .6rem;
}
.cx-hero-title { font-size: 1.5rem; font-weight: 800; line-height: 1.3; }
.cx-hero-sub { font-size: .84rem; color: rgba(255,255,255,.85); margin-top: .35rem; line-height: 1.6; }
.cx-hero-meta { display: flex; flex-wrap: wrap; gap: .5rem; margin-top: .85rem; }
.cx-hero-chip {
  background: rgba(255,255,255,.16);
  border: 1px solid rgba(255,255,255,.22);
  border-radius: 10px;
  padding: .28rem .7rem;
  font-size: .76rem;
  font-weight: 700;
}

/* ─────────────── Body ─────────────── */
.cx-body { max-width: 720px; margin: 0 auto; padding: 1rem .9rem 2rem; }

/* Subscription locked state */
.cx-locked {
  text-align: center; padding: 2.5rem 1.25rem; margin-top: 1rem;
  background: #fff; border-radius: 18px;
  border: 1.5px solid rgba(91,95,239,.12);
  box-shadow: 0 6px 20px rgba(17,24,39,.06);
}
.cx-locked-icon { font-size: 3rem; margin-bottom: .5rem; }
.cx-locked-title { font-size: 1.25rem; font-weight: 800; color: #1A1A1A; margin-bottom: .4rem; }
.cx-locked-msg { font-size: .9rem; color: #6B7280; line-height: 1.7; margin-bottom: 1.1rem; }
.cx-locked-btn {
  display: inline-block; text-decoration: none;
  background: linear-gradient(135deg,#5B5FEF,#7C4DFF); color: #fff;
  font-weight: 700; font-size: .9rem; padding: .6rem 1.4rem; border-radius: 12px;
  box-shadow: 0 8px 20px rgba(91,95,239,.28);
}

/* Branch selector */
.cx-branch-strip {
  display: flex; gap: .5rem; overflow-x: auto; padding: .15rem 0 .55rem;
  margin-bottom: .4rem; scrollbar-width: none;
}
.cx-branch-strip::-webkit-scrollbar { display: none; }
.cx-branch-chip {
  flex: 0 0 auto;
  background: #fff;
  border: 1.5px solid rgba(91,95,239,.14);
  border-radius: 99px;
  padding: .5rem 1.05rem;
  font-family: 'Cairo', sans-serif;
  font-size: .82rem;
  font-weight: 700;
  color: #6B7280;
  cursor: pointer;
  transition: all .2s;
  box-shadow: 0 2px 8px rgba(17,24,39,.05);
}
.cx-branch-chip.active {
  background: linear-gradient(135deg,#5B5FEF,#7C4DFF);
  border-color: transparent; color: #fff;
  box-shadow: 0 8px 20px rgba(91,95,239,.28);
}

/* Section label */
.cx-section-label {
  font-size: .95rem; font-weight: 800; color: #374151;
  margin: 1rem .15rem .6rem; display: flex; align-items: center; gap: .4rem;
}

/* ─────────────── Unit cards ─────────────── */
.cx-unit {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 3px 14px rgba(17,24,39,.06);
  border: 1px solid rgba(91,95,239,.07);
  margin-bottom: .8rem;
  overflow: hidden;
  transition: box-shadow .2s;
}
.cx-unit.expanded { box-shadow: 0 10px 26px rgba(91,95,239,.16); }
.cx-unit-head {
  width: 100%;
  display: flex; align-items: center; gap: .8rem;
  padding: 1rem 1.05rem;
  background: #fff; border: none; cursor: pointer;
  font-family: 'Cairo', sans-serif; text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.cx-unit-icon {
  flex: 0 0 auto;
  width: 42px; height: 42px; border-radius: 13px;
  display: grid; place-items: center; font-size: 1.15rem;
  background: linear-gradient(135deg, rgba(91,95,239,.12), rgba(124,77,255,.1));
}
.cx-unit-titles { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: .15rem; }
.cx-unit-title { font-size: 1rem; font-weight: 800; color: #1A1A1A; }
.cx-unit-sub { font-size: .78rem; font-weight: 600; color: #9CA3AF; }
.cx-chevron {
  flex: 0 0 auto; font-size: 1.1rem; color: #5B5FEF; font-weight: 800;
  transition: transform .28s ease;
}
.cx-unit.expanded .cx-chevron { transform: rotate(180deg); }

/* Lessons inside a unit */
.cx-lessons {
  background: #F7F8FE;
  border-top: 1px solid rgba(91,95,239,.07);
  padding: .4rem;
}
.cx-lesson-row {
  width: 100%;
  display: flex; align-items: center; gap: .7rem;
  padding: .75rem .7rem;
  background: #fff; border: 1px solid rgba(91,95,239,.06);
  border-radius: 12px; margin-bottom: .4rem;
  cursor: pointer; font-family: 'Cairo', sans-serif; text-align: right;
  transition: all .16s; -webkit-tap-highlight-color: transparent;
}
.cx-lesson-row:last-child { margin-bottom: 0; }
.cx-lesson-row:active { transform: scale(.99); background: #F3F4FF; }
.cx-play {
  flex: 0 0 auto; width: 34px; height: 34px; border-radius: 50%;
  display: grid; place-items: center; font-size: .72rem; color: #fff;
  background: linear-gradient(135deg,#5B5FEF,#7C4DFF);
  box-shadow: 0 4px 10px rgba(91,95,239,.3); padding-right: 2px;
}
.cx-lesson-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: .1rem; }
.cx-lesson-title { font-size: .9rem; font-weight: 700; color: #1F2937; }
.cx-lesson-desc {
  font-size: .74rem; color: #9CA3AF;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cx-go { flex: 0 0 auto; color: #C4C8F0; font-size: 1.25rem; font-weight: 800; }

/* Review card variant */
.cx-unit--review {
  border: 1.5px solid rgba(0,192,150,.28);
  background: linear-gradient(180deg, rgba(0,200,160,.06), #fff 60%);
}
.cx-unit--review .cx-unit-icon {
  background: linear-gradient(135deg, rgba(0,200,160,.18), rgba(0,180,200,.14));
}
.cx-unit--review .cx-chevron { color: #00A884; }
.cx-review .cx-section-label { color: #059669; }
.cx-rot { display: inline-block; }

/* ─────────────── Loading / empty ─────────────── */
.cx-loading {
  display: flex; align-items: center; justify-content: center; gap: .6rem;
  padding: 2rem 1rem; color: #6B7280; font-size: .88rem; font-weight: 600;
}
.cx-spinner {
  width: 20px; height: 20px; border-radius: 50%;
  border: 2.5px solid rgba(91,95,239,.2); border-top-color: #5B5FEF;
  animation: cx-spin .7s linear infinite;
}
.cx-spinner--sm { width: 15px; height: 15px; border-width: 2px; }
@keyframes cx-spin { to { transform: rotate(360deg); } }
.cx-empty {
  text-align: center; padding: 3rem 1.5rem; color: #9CA3AF;
}
.cx-empty-icon { font-size: 3rem; margin-bottom: .6rem; }
.cx-empty p { font-size: .9rem; line-height: 1.7; }
.cx-empty-inline {
  display: flex; align-items: center; justify-content: center; gap: .4rem;
  padding: .8rem; color: #9CA3AF; font-size: .82rem; font-weight: 600;
}

/* Expand transition */
.cx-expand-enter-active, .cx-expand-leave-active {
  transition: opacity .2s ease, max-height .28s ease;
  overflow: hidden;
}
.cx-expand-enter-from, .cx-expand-leave-to { opacity: 0; max-height: 0; }
.cx-expand-enter-to, .cx-expand-leave-from { opacity: 1; max-height: 1600px; }

/* ═══════════════ Lesson view ═══════════════ */
.cx-lesson-view { max-width: 720px; margin: 0 auto; }

.cx-topbar {
  position: sticky; top: 0; z-index: 20;
  display: flex; align-items: center; gap: .5rem;
  padding: .7rem .8rem;
  background: rgba(255,255,255,.92);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(91,95,239,.1);
  box-shadow: 0 2px 12px rgba(17,24,39,.05);
}
.cx-back {
  flex: 0 0 auto; width: 38px; height: 38px; border-radius: 12px;
  border: none; background: rgba(91,95,239,.1); color: #5B5FEF;
  font-size: 1.5rem; font-weight: 800; cursor: pointer; line-height: 1;
  display: grid; place-items: center; padding-bottom: 3px;
}
.cx-topbar-title {
  flex: 1; min-width: 0; font-size: .98rem; font-weight: 800; color: #1A1A1A;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* Video / player stage — full width, main focus */
.cx-stage { background: #000; width: 100%; }
.cx-player { width: 100%; }
.cx-player :deep(iframe), .cx-player :deep(video) {
  width: 100% !important; display: block;
}
.cx-pdf { background: #F5F7FB; min-height: 60vh; padding: .6rem; }
/* Split-screen attached PDF sits directly below the video — minimal spacing,
   full width, no desktop-style card. On wider screens keep it readable. */
.cx-video-pdf { margin-top: .35rem; }
@media (min-width: 992px) {
  .cx-video-pdf { max-width: 900px; margin-left: auto; margin-right: auto; }
}
.cx-stage-empty {
  background: #0f1020; color: #9CA3AF; text-align: center;
  padding: 3rem 1rem; min-height: 200px;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: .5rem;
}

/* Player switch */
.cx-switch {
  display: flex; gap: .5rem; padding: .7rem .9rem 0; max-width: 720px; margin: 0 auto;
}
.cx-switch button {
  flex: 1;
  background: #fff; border: 1.5px solid rgba(91,95,239,.22); color: #5B5FEF;
  padding: .55rem .5rem; border-radius: 12px;
  font-family: 'Cairo', sans-serif; font-weight: 700; font-size: .82rem; cursor: pointer;
  transition: all .2s;
}
.cx-switch button.active {
  background: linear-gradient(135deg,#5B5FEF,#7C4DFF); border-color: transparent; color: #fff;
  box-shadow: 0 6px 16px rgba(91,95,239,.25);
}

/* Lesson meta */
.cx-lesson-meta { padding: .9rem .95rem 0; }
.cx-lesson-h { font-size: 1.2rem; font-weight: 800; color: #1A1A1A; }
.cx-lesson-p { font-size: .85rem; color: #6B7280; line-height: 1.7; margin-top: .35rem; }

/* Content list */
.cx-content-list { padding: 0 .9rem; }
.cx-content-list .cx-section-label { margin-top: 1.1rem; }
.cx-content-row {
  width: 100%;
  display: flex; align-items: center; gap: .75rem;
  padding: .8rem .8rem;
  background: #fff; border: 1.5px solid rgba(91,95,239,.08);
  border-radius: 14px; margin-bottom: .55rem;
  cursor: pointer; font-family: 'Cairo', sans-serif; text-align: right;
  transition: all .16s; -webkit-tap-highlight-color: transparent;
}
.cx-content-row:active { transform: scale(.99); }
.cx-content-row.active {
  border-color: rgba(91,95,239,.4);
  background: linear-gradient(135deg, rgba(91,95,239,.08), rgba(124,77,255,.05));
  box-shadow: 0 6px 16px rgba(91,95,239,.14);
}
.cx-content-ic {
  flex: 0 0 auto; width: 40px; height: 40px; border-radius: 12px;
  display: grid; place-items: center;
  background: rgba(91,95,239,.08);
}
.cx-content-ic :deep(svg) { width: 20px; height: 20px; }
.cx-content-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: .12rem; }
.cx-content-title { font-size: .9rem; font-weight: 700; color: #1F2937; }
.cx-content-type { font-size: .74rem; color: #9CA3AF; font-weight: 600; }
.cx-content-state {
  flex: 0 0 auto; font-size: .68rem; font-weight: 800; color: #5B5FEF;
  background: rgba(91,95,239,.1); border-radius: 8px; padding: .2rem .5rem;
}

/* Prev / next */
.cx-nav {
  display: flex; gap: .6rem; padding: 1.1rem .9rem 0;
}
.cx-nav-btn {
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: .15rem; align-items: flex-start;
  background: #fff; border: 1.5px solid rgba(91,95,239,.14);
  border-radius: 14px; padding: .7rem .8rem;
  font-family: 'Cairo', sans-serif; cursor: pointer; text-align: right;
  transition: all .18s;
}
.cx-nav-btn--next { align-items: flex-end; }
.cx-nav-btn:disabled { opacity: .4; cursor: default; }
.cx-nav-btn:not(:disabled):active { transform: scale(.98); border-color: rgba(91,95,239,.35); }
.cx-nav-dir { font-size: .72rem; font-weight: 700; color: #5B5FEF; }
.cx-nav-name {
  font-size: .82rem; font-weight: 700; color: #374151;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;
}

/* ─────────────── Wider screens ─────────────── */
@media (min-width: 768px) {
  .cx-hero-title { font-size: 1.8rem; }
  .cx-unit-title { font-size: 1.05rem; }
}
</style>
